import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { recipes } from '../data/recipes';

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const Heading: React.FC<{ level: HeadingLevel; children: React.ReactNode; className?: string }> = ({
  level,
  children,
  className = ''
}) => {
  const sizeClasses = {
    1: 'font-serif text-2xl font-normal mt-6 mb-3',
    2: 'font-serif text-xl font-normal mt-5 mb-2',
    3: 'font-serif text-lg font-normal mt-4 mb-2',
    4: 'font-serif text-base font-normal mt-3 mb-2',
    5: 'font-serif text-sm font-normal mt-2 mb-1',
    6: 'font-serif text-sm font-normal mt-2 mb-1'
  };

  const Tag = `h${level}` as const;
  return <Tag className={`${sizeClasses[level]} text-stone-900 ${className}`}>{children}</Tag>;
};

const RecipeDetails = () => {
  const { id } = useParams();
  const recipe = recipes.find((r) => r.id === id);

  if (!recipe) {
    return (
      <div className="min-h-screen py-32 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-serif text-4xl font-normal mb-8 text-stone-900">Recipe not found</h1>
          <Link to="/recipes" className="font-serif text-stone-800 hover:underline">
            ← Back to recipes
          </Link>
        </div>
      </div>
    );
  }

  // Helper function to detect and parse markdown headings
  const parseHeading = (text: string) => {
    const match = text.match(/^(#{1,6})\s*(.+)$/);
    if (match) {
      const level = match[1].length; // Number of # symbols
      const content = match[2]; // Text after the #'s
      return { level, content };
    }
    return null;
  };

  // Helper to render inline **bold** markers as <strong>
  const renderInlineFormatting = (text: string) => {
    // quick return for falsy strings
    if (!text) return text;
    const parts: React.ReactNode[] = [];
    const regex = /\*\*(.+?)\*\*/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const [fullMatch, content] = match;
      const idx = match.index;
      if (idx > lastIndex) {
        parts.push(text.slice(lastIndex, idx));
      }
      parts.push(<strong key={parts.length}>{content}</strong>);
      lastIndex = idx + fullMatch.length;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    // if no bold markers, return original string to keep markup simple
    return parts.length === 0 ? text : parts;
  };

  // Helper function to render ingredient items
  const renderIngredient = (ing: string, index: number) => {
    // Skip empty strings
    if (!ing.trim()) {
      return null;
    }
    
    // Check if it's a heading
    const heading = parseHeading(ing);
    if (heading) {
      return (
        <Heading 
          key={index} 
          level={Math.min(heading.level, 6) as HeadingLevel}
        >
          {renderInlineFormatting(heading.content)}
        </Heading>
      );
    }
    
    // Regular ingredient - render as list item
    return (
      <li key={index} className="font-serif text-stone-500 mb-1">
        {renderInlineFormatting(ing)}
      </li>
    );
  };

  // Helper function to render step items
  const renderStep = (step: string, index: number) => {
    // Skip empty strings
    if (!step.trim()) {
      return null;
    }
    
    // Check if it's a heading
    const heading = parseHeading(step);
    if (heading) {
      return (
        <Heading 
          key={index} 
          level={Math.min(heading.level, 6) as HeadingLevel}
        >
          {renderInlineFormatting(heading.content)}
        </Heading>
      );
    }
    
    // Regular step - render as list item
    return (
      <li key={index} className="font-serif text-stone-500 mb-2">
        {renderInlineFormatting(step)}
      </li>
    );
  };

  return (
    <div className="min-h-screen py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/recipes" className="font-serif text-stone-800 hover:underline mb-8 inline-block">
            ← Back to recipes
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              {recipe.imageUrl && (
                <img
                  src={recipe.imageUrl}
                  alt={recipe.title}
                  className="w-full rounded-lg shadow-lg"
                />
              )}
            </div>
            <div>
              <h1 className="font-serif text-4xl font-normal mb-6 text-stone-900">{recipe.title}</h1>
              {recipe.subtitle && (
                <p className="font-serif text-lg text-stone-500 mb-6">{recipe.subtitle}</p>
              )}
              <div className="flex gap-4 mb-8">
                <span className="font-serif px-3 py-1 text-sm bg-stone-100 text-stone-800 rounded">{recipe.cuisine}</span>
                <span className="font-serif px-3 py-1 text-sm bg-stone-100 text-stone-800 rounded">{recipe.cookingTime}</span>
              </div>

              <div className="mb-8">
                <h2 className="font-serif text-2xl font-normal mb-4 text-stone-900">Ingredients</h2>
                <div>
                  {recipe.ingredients.map((ing, i) => renderIngredient(ing, i))}
                </div>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-normal mb-4 text-stone-900">Steps</h2>
                <div>
                  {recipe.steps.map((step, i) => renderStep(step, i))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RecipeDetails;